import { useTranslation } from 'react-i18next';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import {
  buildMonthGrid,
  isSameDay,
  startOfDay,
  type LegacyDateRange,
} from '../lib/date-range';

/**
 * The month calendar: a Monday-first six-week grid with 44px circular day
 * cells, measured off the reference. Days outside the displayed month render
 * greyed but stay selectable, as the original's did.
 *
 * Selection is a RANGE: the first click sets the start and collapses the range
 * onto it, the second sets the end (swapping if the user picked backwards).
 * That is the old picker's behavior and it is why a half-made selection shows
 * as a single highlighted day rather than nothing.
 */
const WEEKDAY_KEYS = [
  'date.mo',
  'date.tu',
  'date.we',
  'date.th',
  'date.fr',
  'date.sa',
  'date.su',
] as const;

interface LegacyCalendarProps {
  /** The month being displayed (any day within it). */
  month: Date;
  range: LegacyDateRange;
  /** Days after this are unselectable — the old picker refused future dates. */
  maxDate?: Date;
  onMonthChange: (month: Date) => void;
  onSelectDay: (day: Date) => void;
}

export function LegacyCalendar({
  month,
  range,
  maxDate,
  onMonthChange,
  onSelectDay,
}: LegacyCalendarProps) {
  const { t, i18n } = useTranslation('legacy');

  const grid = buildMonthGrid(month);
  const monthLabel = new Intl.DateTimeFormat(i18n.language, {
    month: 'long',
    year: 'numeric',
  }).format(month);

  const shiftMonth = (delta: number) => {
    const next = new Date(month);
    next.setDate(1);
    next.setMonth(next.getMonth() + delta);
    onMonthChange(next);
  };

  return (
    <div className="flex w-[365px] max-w-full shrink-0 flex-col gap-4 rounded-[24px] bg-[var(--lg-surface)] p-[30px]">
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={() => shiftMonth(-1)}
          aria-label={t('date.mm')}
          className="cursor-pointer text-[var(--lg-orange-500)] transition-opacity hover:opacity-70"
        >
          <ChevronLeftIcon className="size-6" />
        </button>
        <span className="text-[16px] font-medium text-[var(--lg-fg)] capitalize">
          {monthLabel}
        </span>
        <button
          type="button"
          onClick={() => shiftMonth(1)}
          aria-label={t('date.mm')}
          className="cursor-pointer text-[var(--lg-orange-500)] transition-opacity hover:opacity-70"
        >
          <ChevronRightIcon className="size-6" />
        </button>
      </div>

      <div className="grid grid-cols-7 gap-y-1">
        {WEEKDAY_KEYS.map((key) => (
          <span
            key={key}
            className="flex h-7 items-center justify-center text-[16px] font-medium text-[var(--lg-fg)]"
          >
            {t(key)}
          </span>
        ))}

        {grid.map((day) => {
          const isCurrentMonth = day.getMonth() === month.getMonth();
          const isStart = isSameDay(day, range.from);
          const isEnd = isSameDay(day, range.to);
          const isEdge = isStart || isEnd;
          const isBetween = day > range.from && day < range.to;
          const isDisabled =
            maxDate !== undefined && startOfDay(day) > startOfDay(maxDate);

          return (
            <button
              key={day.toISOString()}
              type="button"
              disabled={isDisabled}
              onClick={() => onSelectDay(day)}
              aria-pressed={isEdge}
              className={[
                'flex size-11 items-center justify-center rounded-full text-[16px] font-medium',
                'transition-colors not-disabled:cursor-pointer',
                isEdge
                  ? 'bg-[var(--lg-orange-500)] text-[var(--lg-surface)]'
                  : isBetween
                    ? 'bg-[var(--lg-orange-500)]/10 text-[var(--lg-fg)]'
                    : isCurrentMonth
                      ? 'text-[var(--lg-fg)] hover:bg-[var(--lg-grey-100)]'
                      : 'text-[var(--lg-grey-400)] hover:bg-[var(--lg-grey-100)]',
                isDisabled ? 'opacity-40' : '',
              ].join(' ')}
            >
              {day.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );
}
